import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Avatar, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress, FormControlLabel,
  Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment,
  MenuItem, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Add, Apple, Computer, ContentCopy, DeleteOutline, Devices, EditOutlined,
  OpenInNew, Refresh, Search, Star, StarBorder, SupportAgent, LockOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/ui/PageHeader';
import { ComputerInput, RemoteComputer, remoteComputerService } from '../services/remoteComputers';
import { copyTextToClipboard, promptManualCopy } from '../utils/clipboard';

const emptyComputer: ComputerInput = {
  name: '', rustdeskId: '', platform: 'windows', groupName: '', clientName: '', notes: '', favorite: false,
  password: '', clearPassword: false,
};
const platformLabels = { windows: 'Windows', macos: 'Mac', linux: 'Linux', other: 'Altro' };
const errorMessage = (error: any) => error.response?.data?.error?.message || 'Operazione non riuscita. Riprova.';

const MyComputersPage: React.FC = () => {
  const navigate = useNavigate();
  const [computers, setComputers] = useState<RemoteComputer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [search, setSearch] = useState('');
  const [group, setGroup] = useState('');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ComputerInput>(emptyComputer);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<RemoteComputer | null>(null);
  const [connecting, setConnecting] = useState<RemoteComputer | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { setComputers(await remoteComputerService.list()); }
    catch (e) { setError(errorMessage(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const groups = useMemo(() => Array.from(new Set(computers.map(pc => pc.groupName).filter(Boolean))).sort(), [computers]);
  const filtered = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    return computers.filter(pc => (!group || pc.groupName === group) && (!favoritesOnly || pc.favorite)
      && (!query || [pc.name, pc.rustdeskId, pc.clientName, pc.groupName, pc.notes].some(value => value.toLocaleLowerCase().includes(query))))
      .sort((a, b) => Number(b.favorite) - Number(a.favorite) || a.name.localeCompare(b.name, 'it'));
  }, [computers, search, group, favoritesOnly]);

  const edit = (pc?: RemoteComputer) => {
    setEditingId(pc?.id || null);
    setForm(pc ? { name: pc.name, rustdeskId: pc.rustdeskId, platform: pc.platform, groupName: pc.groupName,
      clientName: pc.clientName, notes: pc.notes, favorite: pc.favorite, password: '', clearPassword: false } : { ...emptyComputer });
    setFormError(''); setDialogOpen(true);
  };
  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    const input = { ...form, name: form.name.trim(), rustdeskId: form.rustdeskId.replace(/\s/g, '') };
    if (!input.name || !/^\d{6,20}$/.test(input.rustdeskId)) {
      setFormError('Inserisci il nome e un ID RustDesk numerico da 6 a 20 cifre.'); return;
    }
    setSaving(true); setFormError('');
    try {
      const pc = editingId ? await remoteComputerService.update(editingId, input) : await remoteComputerService.create(input);
      setComputers(previous => editingId ? previous.map(item => item.id === editingId ? pc : item) : [...previous, pc]);
      setDialogOpen(false); setNotice(editingId ? 'PC aggiornato.' : 'PC aggiunto alla tua rubrica.');
      setForm({ ...emptyComputer });
    } catch (e) { setFormError(errorMessage(e)); }
    finally { setSaving(false); }
  };
  const toggleFavorite = async (pc: RemoteComputer) => {
    setBusyId(pc.id); setError('');
    try {
      const updated = await remoteComputerService.update(pc.id, { ...pc, favorite: !pc.favorite });
      setComputers(previous => previous.map(item => item.id === pc.id ? updated : item));
    } catch (e) { setError(errorMessage(e)); }
    finally { setBusyId(null); }
  };
  const remove = async () => {
    if (!deleting) return;
    setBusyId(deleting.id); setError('');
    try {
      await remoteComputerService.remove(deleting.id);
      setComputers(previous => previous.filter(pc => pc.id !== deleting.id));
      setDeleting(null); setNotice('PC rimosso dalla rubrica.');
    } catch (e) { setError(errorMessage(e)); }
    finally { setBusyId(null); }
  };
  const copyId = async (pc: RemoteComputer) => {
    if (await copyTextToClipboard(pc.rustdeskId)) setNotice('ID RustDesk copiato.');
    else promptManualCopy(pc.rustdeskId, 'ID RustDesk');
  };
  const connect = async (pc: RemoteComputer) => {
    setBusyId(pc.id); setError('');
    try {
      const credentials = await remoteComputerService.connect(pc.id);
      setConnecting(pc);
      const id = encodeURIComponent(credentials.rustdeskId);
      const query = credentials.password ? `?password=${encodeURIComponent(credentials.password)}` : '';
      // Fetch credentials only on request and hand them directly to the local app.
      // Never keep fetched credentials in card markup, React state or browser storage.
      window.location.assign(`rustdesk://${id}${query}`);
    } catch (e) { setError(errorMessage(e)); }
    finally { setBusyId(null); }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1500, mx: 'auto' }}>
      <PageHeader title="I miei PC" subtitle="I tuoi computer, sempre a portata di mano. Rubrica privata sincronizzata con il tuo account."
        actions={<>
          <Button startIcon={<SupportAgent />} onClick={() => navigate('/remote-assistance')}>Installa RustDesk</Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => edit()}>Aggiungi PC</Button>
        </>} />
      <Stack spacing={2}>
        {error && <Alert severity="error" action={<Button color="inherit" onClick={load}>Riprova</Button>}>{error}</Alert>}
        {notice && <Alert severity="success" onClose={() => setNotice('')}>{notice}</Alert>}
        <Card><CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <TextField label="Cerca nei miei PC" placeholder="Nome, ID o cliente" value={search} onChange={e => setSearch(e.target.value)}
              size="small" sx={{ flex: 1 }} InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }} />
            <TextField select label="Gruppo" value={group} onChange={e => setGroup(e.target.value)} size="small" sx={{ minWidth: 180 }}>
              <MenuItem value="">Tutti i gruppi</MenuItem>
              {Array.from(new Set([...groups, ...(group ? [group] : [])])).map(name => <MenuItem key={name} value={name}>{name}</MenuItem>)}
            </TextField>
            <Button variant={favoritesOnly ? 'contained' : 'outlined'} startIcon={<Star />} aria-pressed={favoritesOnly}
              onClick={() => setFavoritesOnly(!favoritesOnly)}>Preferiti</Button>
            <Tooltip title="Aggiorna rubrica"><span><IconButton aria-label="Aggiorna rubrica" onClick={load} disabled={loading || !!busyId}><Refresh /></IconButton></span></Tooltip>
          </Stack>
        </CardContent></Card>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Typography variant="body2" color="text.secondary">{filtered.length} di {computers.length} PC</Typography>
          <Typography variant="body2" color="text.secondary">Disponibilità verificata da RustDesk alla connessione</Typography>
        </Stack>
        {loading ? <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress aria-label="Caricamento dei PC" /></Box>
          : !computers.length && !error ? <Card><CardContent sx={{ py: 6, textAlign: 'center' }}>
            <Devices sx={{ fontSize: 56, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>Il tuo primo PC, a un clic di distanza</Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>Apri RustDesk sul computer da aggiungere e copia il suo ID.</Typography>
            <Button variant="contained" startIcon={<Add />} onClick={() => edit()}>Aggiungi il primo PC</Button>
          </CardContent></Card>
          : !filtered.length && !error ? <Alert severity="info">Nessun PC corrisponde ai filtri. Prova un’altra ricerca o seleziona tutti i gruppi.</Alert>
          : <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' }, gap: 2 }}>
            {filtered.map(pc => <Card key={pc.id} sx={{ display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flex: 1 }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'action.hover', color: 'primary.main', width: 48, height: 48 }}>{pc.platform === 'macos' ? <Apple /> : <Computer />}</Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}><Typography variant="h6" sx={{ overflowWrap: 'anywhere' }}>{pc.name}</Typography>
                    <Typography variant="body2" color="text.secondary">{platformLabels[pc.platform]}</Typography></Box>
                  <IconButton aria-label={`${pc.favorite ? 'Rimuovi' : 'Aggiungi'} ${pc.name} ${pc.favorite ? 'dai' : 'ai'} preferiti`}
                    disabled={!!busyId} onClick={() => toggleFavorite(pc)} color={pc.favorite ? 'warning' : 'default'}>{pc.favorite ? <Star /> : <StarBorder />}</IconButton>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">ID RustDesk</Typography>
                  <Typography sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{pc.rustdeskId}</Typography>
                  <IconButton size="small" aria-label={`Copia ID di ${pc.name}`} onClick={() => copyId(pc)}><ContentCopy fontSize="small" /></IconButton>
                </Stack>
                {pc.clientName && <Typography variant="body2" sx={{ mb: 1, overflowWrap: 'anywhere' }}>Cliente: {pc.clientName}</Typography>}
                {pc.groupName && <Chip size="small" label={pc.groupName} sx={{ mb: 1, maxWidth: '100%' }} />}
                {pc.hasPassword && <Chip size="small" icon={<LockOutlined />} label="Password salvata" sx={{ mb: 1, ml: pc.groupName ? 1 : 0 }} />}
                {pc.notes && <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{pc.notes}</Typography>}
              </CardContent>
              <Stack direction="row" spacing={1} sx={{ px: 2, pb: 2 }}>
                <Button variant="contained" fullWidth startIcon={<OpenInNew />} disabled={!!busyId} onClick={() => connect(pc)}>Collegati</Button>
                <Tooltip title="Modifica PC"><span><IconButton aria-label={`Modifica ${pc.name}`} disabled={!!busyId} onClick={() => edit(pc)}><EditOutlined /></IconButton></span></Tooltip>
                <Tooltip title="Rimuovi dalla rubrica"><span><IconButton aria-label={`Rimuovi ${pc.name}`} disabled={!!busyId} onClick={() => setDeleting(pc)}><DeleteOutline /></IconButton></span></Tooltip>
              </Stack>
            </Card>)}
          </Box>}
      </Stack>

      <Dialog open={dialogOpen} onClose={() => { if (!saving) { setDialogOpen(false); setForm({ ...emptyComputer }); } }} maxWidth="sm" fullWidth>
        <Box component="form" onSubmit={save}>
          <DialogTitle>{editingId ? 'Modifica PC' : 'Aggiungi PC'}</DialogTitle>
          <DialogContent><Stack spacing={2} sx={{ pt: 1 }}>
            {formError && <Alert severity="error">{formError}</Alert>}
            <TextField autoFocus label="Nome del PC" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} inputProps={{ maxLength: 100 }} />
            <TextField label="ID RustDesk" required value={form.rustdeskId} onChange={e => setForm({ ...form, rustdeskId: e.target.value })}
              helperText="Copia l’ID mostrato da RustDesk sul computer da controllare." inputProps={{ maxLength: 40, inputMode: 'numeric' }} />
            <TextField label="Password RustDesk (facoltativa)" type="password" autoComplete="new-password" value={form.password || ''}
              disabled={form.clearPassword} onChange={e => setForm({ ...form, password: e.target.value })} inputProps={{ maxLength: 256 }}
              helperText={computers.find(pc => pc.id === editingId)?.hasPassword
                ? 'Password già salvata. Lascia vuoto per conservarla oppure inserisci una nuova password.'
                : 'Inserisci la password permanente impostata in RustDesk sul PC remoto.'} />
            {computers.find(pc => pc.id === editingId)?.hasPassword && <FormControlLabel label="Rimuovi la password salvata"
              control={<Checkbox checked={!!form.clearPassword} onChange={e => setForm({ ...form, clearPassword: e.target.checked, password: '' })} />} />}
            <TextField select label="Sistema operativo" value={form.platform} onChange={e => setForm({ ...form, platform: e.target.value as ComputerInput['platform'] })}>
              {Object.entries(platformLabels).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
            </TextField>
            <TextField label="Cliente (facoltativo)" value={form.clientName} onChange={e => setForm({ ...form, clientName: e.target.value })} inputProps={{ maxLength: 100 }} />
            <TextField label="Gruppo (facoltativo)" placeholder="Es. Ufficio, Produzione, Assistenza" value={form.groupName} onChange={e => setForm({ ...form, groupName: e.target.value })} inputProps={{ maxLength: 80 }} />
            <TextField label="Note (facoltative)" multiline minRows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} inputProps={{ maxLength: 1000 }} />
            <Typography variant="body2" color="text.secondary">La password viene salvata cifrata nella tua rubrica e usata quando premi Collegati.</Typography>
          </Stack></DialogContent>
          <DialogActions><Button disabled={saving} onClick={() => { setDialogOpen(false); setForm({ ...emptyComputer }); }}>Annulla</Button><Button type="submit" variant="contained" disabled={saving}>{saving ? 'Salvataggio…' : 'Salva PC'}</Button></DialogActions>
        </Box>
      </Dialog>
      <Dialog open={!!deleting} onClose={() => { if (!busyId) setDeleting(null); }} maxWidth="xs" fullWidth>
        <DialogTitle>Rimuovere {deleting?.name}?</DialogTitle>
        <DialogContent><Typography>Il PC sarà rimosso dalla tua rubrica. RustDesk resterà installato sul computer.</Typography></DialogContent>
        <DialogActions><Button disabled={!!busyId} onClick={() => setDeleting(null)}>Annulla</Button><Button color="error" disabled={!!busyId} onClick={remove}>Rimuovi</Button></DialogActions>
      </Dialog>
      <Dialog open={!!connecting} onClose={() => setConnecting(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Collegamento a {connecting?.name}</DialogTitle>
        <DialogContent><Stack spacing={2}>
          <Typography>{connecting?.hasPassword
            ? 'Conferma l’apertura di RustDesk nel browser. La connessione usa la password salvata.'
            : 'Conferma l’apertura di RustDesk nel browser, quindi inserisci la password del PC o chiedi a chi è davanti al computer di accettare.'}</Typography>
          <Typography variant="body2" color="text.secondary">Se l’app non si apre, installa CM Assistenza oppure copia l’ID e inseriscilo in RustDesk.</Typography>
          <Button startIcon={<ContentCopy />} onClick={() => connecting && copyId(connecting)}>Copia ID: {connecting?.rustdeskId}</Button>
          <Button disabled={!!busyId} onClick={() => connecting && connect(connecting)}>Apri RustDesk</Button>
          <Button onClick={() => { setConnecting(null); navigate('/remote-assistance'); }}>Scarica CM Assistenza</Button>
        </Stack></DialogContent>
        <DialogActions><Button onClick={() => setConnecting(null)}>Chiudi</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyComputersPage;
